import { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  Collapse,
  FormControlLabel,
  InputAdornment,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Alert,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import AdminFamilySelect from "components/creation/AdminFamilySelect";
import Checkbox from "components/common/Checkbox";
import GridPage from "components/layout/GridPage";
import useIsMobile from "components/hooks/useIsMobile";
import { addDays } from "date-fns";
import { ChevronDown, Copy, Download, Pencil } from "lucide-react";
import { useRunWorkflow } from "services/workflow";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  RunWorkflowRequest,
  RunWorkflowResponse,
} from "protogen/workflow_service_pb";
import {
  LoadDocument,
  LoadDocumentMetadata,
} from "components/creation/LoadDocument";
import DocumentViewer from "components/editor/DocumentViewer";
import { CurrentUserContext } from "components/context/RequireAuth";
import { isUserAdmin } from "common/userUtils";
import { useCreateDocument } from "services/documents";
import { DocumentType } from "protogen/documents_pb";
import FamilySelect from "components/creation/FamilySelect";
import { ReactComponent as SparklesIcon } from "../../icons/Advisor/Sparkles.svg";
import { useSnackpack } from "components/context/SnackpackContextProvider";
import SamplePrompts from "components/creation/SamplePrompts";
import FlexBox from "components/common/FlexBox";
import ThreeDotsActiveButton from "components/common/ThreeDotsActiveButton";

const PromptSection = ({
  open,
  query,
  setQuery,
  handleClick,
  loading,
  disabled,
}: {
  open: boolean;
  query: string;
  setQuery: (query: string) => void;
  handleClick: () => void;
  loading: boolean;
  disabled: boolean;
}) => {
  return (
    <Collapse in={open} timeout={200}>
      <Box sx={{ position: "relative" }}>
        <TextField
          fullWidth
          multiline
          minRows={3} // Adjust as needed for initial height
          variant="outlined"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Write a prompt describing the inspiration you want to generate for your client."
          InputProps={{
            endAdornment: (
              <InputAdornment
                position="end"
                sx={{
                  position: "absolute",
                  bottom: 16, // Adjust for padding within TextField
                  right: 16, // Adjust for padding within TextField
                  // Ensure adornment doesn't shrink parent
                  height: "auto",
                  alignItems: "flex-end",
                }}
              >
                <ThreeDotsActiveButton
                  size="small"
                  onClick={handleClick}
                  disabled={disabled}
                  active={loading}
                >
                  Run prompt
                </ThreeDotsActiveButton>
              </InputAdornment>
            ),
            sx: {
              // Add padding to the bottom of the input to make space for the button
              paddingBottom: "50px", // Adjust as needed
            },
          }}
        />
      </Box>
    </Collapse>
  );
};

const daysBack = (date: Date) => {
  return Math.round(
    (new Date().getTime() - date.getTime()) / (1000 * 60 * 60 * 24),
  );
};

const defaultDate = () => addDays(new Date(), -30);

const ConfigurationSection = ({
  title,
  description,
  content,
}: {
  title: string;
  description?: string;
  content: React.ReactNode;
}) => {
  const formLeftStyle = { flex: 2 };
  const formRightStyle = { flex: 5 };
  const isMobile = useIsMobile();

  return (
    <FlexBox direction={isMobile ? "column" : "row"}>
      <FlexBox direction="column" gap="2px" sx={{ ...formLeftStyle }}>
        <Typography
          variant="bodyHeavy"
          sx={{
            marginTop: description ? 0 : isMobile ? "0" : "16px",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </Typography>
        {description && (
          <Typography variant="bodySmall" sx={{ paddingRight: "4px" }}>
            {description}
          </Typography>
        )}
      </FlexBox>

      <FlexBox sx={{ ...formRightStyle }}>{content}</FlexBox>
    </FlexBox>
  );
};

type ModelCost = {
  name: string;
  label: string;
  inputPerMTokens: number; // USD per 1M input tokens
  outputPerMTokens: number; // USD per 1M output tokens
};

const MODELS: ModelCost[] = [
  // --- 4o family ---
  {
    name: "gpt-4o",
    label: "gpt-4o",
    inputPerMTokens: 5.0,
    outputPerMTokens: 15.0,
  },
  {
    name: "gpt-4o-mini",
    label: "gpt-4o-mini",
    inputPerMTokens: 1.1,
    outputPerMTokens: 4.4,
  },

  // --- 4.1 family ---
  {
    name: "gpt-4.1",
    label: "gpt-4.1",
    inputPerMTokens: 2.0,
    outputPerMTokens: 8.0,
  },
  {
    name: "gpt-4.1-mini",
    label: "gpt-4.1-mini",
    inputPerMTokens: 0.4,
    outputPerMTokens: 2.0,
  },
  {
    name: "gpt-4.1-nano",
    label: "gpt-4.1-nano",
    inputPerMTokens: 0.1,
    outputPerMTokens: 0.4,
  },
  // -- 5 family --
  {
    name: "gpt-5",
    label: "GPT5",
    inputPerMTokens: 1.25,
    outputPerMTokens: 10.0,
  },
  {
    name: "gpt-5-mini",
    label: "GPT5 Mini",
    inputPerMTokens: 0.25,
    outputPerMTokens: 2.0,
  },
  {
    name: "gpt-5-nano",
    label: "GPT5 Nano",
    inputPerMTokens: 0.05,
    outputPerMTokens: 0.4,
  },

  // --- Reasoning models ---
  // o4
  {
    name: "o4-mini",
    label: "o4-mini (thinking)",
    inputPerMTokens: 2.0,
    outputPerMTokens: 8.0,
  },
  // o3
  {
    name: "o3",
    label: "o3 (expensive)",
    inputPerMTokens: 0.4,
    outputPerMTokens: 1.6,
  },
];

// Helper method to run workflow with polling
const runWorkflowWithPolling = async (
  workflowRequest: (
    payload: RunWorkflowRequest,
  ) => Promise<RunWorkflowResponse | null>,
  request: RunWorkflowRequest,
  pollIntervalMsStart: number = 500,
  pollIntervalMsMax: number = 2500,
  maxPollTimeMs: number = 120000,
): Promise<string | undefined> => {
  // Start the async workflow
  const asyncRequest = new RunWorkflowRequest({
    ...request,
    runAsync: true,
  });

  await workflowRequest(asyncRequest);

  // Now poll for results using onlyUseCache
  const cacheRequest = new RunWorkflowRequest({
    ...request,
    onlyUseCache: true,
  });

  const startTime = Date.now();
  let pollIntervalMs = pollIntervalMsStart;
  while (Date.now() - startTime < maxPollTimeMs) {
    const response = await workflowRequest(cacheRequest);

    if (response?.result && response.result !== "") {
      return response.result;
    }
    // Exponential backoff - increase poll interval by 10% until max.
    pollIntervalMs = Math.min(pollIntervalMs * 1.1, pollIntervalMsMax);
    // Wait before next poll
    await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
  }

  throw new Error(`Workflow timed out after ${maxPollTimeMs}ms`);
};

export default ({ adminMode }: { adminMode: boolean }) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useContext(CurrentUserContext);
  const isAdmin = isUserAdmin(currentUser);
  if (adminMode && !isAdmin) {
    return (
      <Alert severity="error">
        You are not authorized to access this page.
      </Alert>
    );
  }

  // Initialize state from URL params or defaults
  const [selectedFamily, setSelectedFamily] = useState<string | null>(
    searchParams.get("family") || null,
  );
  const [addTasks, setAddTasks] = useState(
    searchParams.get("addTasks") !== "false",
  );
  const [addFacts, setAddFacts] = useState(
    searchParams.get("addFacts") !== "false",
  );
  const [addNotes, setAddNotes] = useState(
    searchParams.get("addNotes") !== "false",
  );
  const [addTaskActivity, setAddTaskActivity] = useState(
    searchParams.get("addTaskActivity") !== "false",
  );
  const [eventsWeeksForward, setEventsWeeksForward] = useState<number>(() => {
    const weeksParam = searchParams.get("eventsWeeksForward");
    return weeksParam ? parseInt(weeksParam, 10) : 0;
  });
  const [selectedModel, setSelectedModel] = useState<string>(() => {
    return searchParams.get("model") || "";
  });
  const [selectedDate, setSelectedDate] = useState<Date | null>(() => {
    const dateParam = searchParams.get("date");
    if (dateParam) {
      const parsed = new Date(dateParam);
      return !isNaN(parsed.getTime()) ? parsed : null;
    }
    return null;
  });
  const { pushSnack } = useSnackpack();
  const [selectedDocument, setSelectedDocument] =
    useState<LoadDocumentMetadata | null>(null);
  const [documentId, setDocumentId] = useState<string>(() => {
    return searchParams.get("documentId") || "";
  });
  const [output, setOutput] = useState<string>("");
  const [documentData, setDocumentData] = useState<{
    content: string;
    contentType: string;
    title: string;
  } | null>(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [promptOpen, setPromptOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [prompt, setPrompt] = useState(searchParams.get("prompt") || "");
  const [runMode, setRunMode] = useState<"context" | "execute">("context");
  const [configurationOpen, setConfigurationOpen] = useState(adminMode);
  const { request: workflowRequest } = useRunWorkflow();
  const { request: createDocumentRequest, loading: createDocumentLoading } =
    useCreateDocument();

  // Update URL when state changes
  useEffect(() => {
    const params = new URLSearchParams();

    if (selectedFamily) {
      params.set("family", selectedFamily);
    }

    if (!addTasks) {
      params.set("addTasks", "false");
    }

    if (!addFacts) {
      params.set("addFacts", "false");
    }

    if (!addNotes) {
      params.set("addNotes", "false");
    }

    if (!addTaskActivity) {
      params.set("addTaskActivity", "false");
    }

    if (eventsWeeksForward > 0) {
      params.set("eventsWeeksForward", eventsWeeksForward.toString());
    }

    if (selectedModel) {
      params.set("model", selectedModel);
    }

    if (selectedDate) {
      params.set("date", selectedDate.toISOString());
    }

    if (prompt) {
      params.set("prompt", prompt);
    }

    if (documentId) {
      params.set("documentId", documentId);
    }
    setSearchParams(params, { replace: true });
  }, [
    selectedFamily,
    addTasks,
    addFacts,
    addNotes,
    addTaskActivity,
    eventsWeeksForward,
    selectedModel,
    selectedDate,
    prompt,
    documentId,
    setSearchParams,
  ]);

  useEffect(() => {
    // Autorun
    if (searchParams.get("autoRun")) {
      runPrompt();
    }
  }, []);

  const handleSubmit = async (
    workflowName: string,
    args?: any[],
  ): Promise<string | undefined> => {
    if (loading) return undefined;
    setOutput("");
    setDocumentData(null);
    setLoading(true);

    const request = new RunWorkflowRequest({
      workflowName: workflowName,
      arguments: [
        {
          name: "communication_days_back",
          value: {
            case: "intValue",
            value: selectedDate ? daysBack(selectedDate) : 30,
          },
        },
        {
          name: "include_tasks",
          value: { case: "boolValue", value: addTasks },
        },
        {
          name: "include_facts",
          value: { case: "boolValue", value: addFacts },
        },
        {
          name: "include_notes",
          value: { case: "boolValue", value: addNotes },
        },
        {
          name: "include_task_activity",
          value: { case: "boolValue", value: addTaskActivity },
        },
        {
          name: "events_days_forward",
          value: { case: "intValue", value: eventsWeeksForward * 7 },
        },
        ...(args || []),
      ],
      entityType: 0, // Not needed here.
      entityRef: selectedFamily || "",
      runAsync: false, // Will be set to true in helper method
    });

    const result = await runWorkflowWithPolling(workflowRequest, request);

    if (adminMode) {
      setPromptOpen(false);
    }
    setLoading(false);
    return result;
  };

  const runContext = async () => {
    if (!adminMode) return;
    setRunMode("context");
    const result = await handleSubmit("family-context");
    if (result) {
      const json = JSON.parse(result);
      setOutput(`Prompt:\n${json.prompt}`);
    }
  };

  const runPrompt = async () => {
    setRunMode("execute");
    const result = await handleSubmit("family-context-prompted", [
      {
        name: "extra_context",
        value: { case: "stringValue", value: prompt },
      },
      ...(selectedModel && adminMode
        ? [
            {
              name: "model",
              value: { case: "stringValue", value: selectedModel },
            },
          ]
        : []),
      ...(selectedDocument
        ? [
            {
              name: "output_template",
              value: {
                case: "stringValue",
                value: `documents:${selectedDocument.id}?use_rich=true`,
              },
            },
          ]
        : []),
    ]);
    if (result) {
      const json = JSON.parse(result);

      // Set document state if document content is present
      if (
        json.document_content &&
        json.document_content_type &&
        json.document_title
      ) {
        setDocumentData({
          content: json.document_content,
          contentType: json.document_content_type,
          title: json.document_title,
        });
      }
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(output);
      pushSnack("Copied to clipboard");
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const handleDocumentCopy = async () => {
    try {
      if (documentData) {
        if (documentData.contentType === "html") {
          const element = document.querySelector(
            "#document-viewer .ProseMirror",
          );
          const plainText = element?.textContent || "";

          const clipboardItem = new ClipboardItem({
            "text/plain": new Blob([plainText], { type: "text/plain" }),
            "text/html": new Blob([documentData.content], {
              type: "text/html",
            }),
          });

          await navigator.clipboard.write([clipboardItem]);
        } else {
          await navigator.clipboard.writeText(documentData.content);
        }
      }
    } catch (err) {
      console.error("Failed to copy document: ", err);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([output], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `family-context-${runMode}-${
      new Date().toISOString().split("T")[0]
    }.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // const handleDocumentDownload = () => {
  //   if (!documentData) return;

  //   const blob = new Blob([documentData.content], {
  //     type: documentData.contentType || "text/plain",
  //   });
  //   const url = URL.createObjectURL(blob);
  //   const a = document.createElement("a");
  //   a.href = url;

  //   // Determine file extension based on content type
  //   let extension = ".txt";
  //   if (documentData.contentType.includes("html")) {
  //     extension = ".html";
  //   } else if (documentData.contentType.includes("markdown")) {
  //     extension = ".md";
  //   }

  //   a.download = `${documentData.title
  //     .replace(/[^a-z0-9]/gi, "_")
  //     .toLowerCase()}${extension}`;
  //   document.body.appendChild(a);
  //   a.click();
  //   document.body.removeChild(a);
  //   URL.revokeObjectURL(url);
  // };

  const handleDocumentEdit = async () => {
    if (!documentData || !selectedFamily) return;
    const doc = await createDocumentRequest({
      familyRef: selectedFamily,
      title: documentData.title,
      textContent: documentData.content,
      content: documentData.content,
      attachments: [],
      documentType: DocumentType.NOTE,
      topics: [],
    });
    if (doc) {
      navigate(`/documents/${encodeURIComponent(doc.document?.uuid || "")}`);
    }
  };

  const handleShareLinkCopy = async () => {
    try {
      // Remove family context from the current url and share.
      const url = new URL(window.location.href);
      url.searchParams.delete("family");
      const currentUrl = url.toString();
      await navigator.clipboard.writeText(currentUrl);
      pushSnack("Copied to clipboard");
    } catch (err) {
      console.error("Failed to copy share link: ", err);
    }
  };

  const hasResults = documentData || output;

  const formLeftStyle = { flex: 2 };
  const formRightStyle = { flex: 5 };

  return (
    <GridPage
      sx={{
        margin: isMobile ? "" : "auto",
        maxWidth: "1000px",
        padding: isMobile ? "20px" : "64px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "24px",
        }}
      >
        <FlexBox direction="column" gap="8px">
          <Typography variant="title" id="tableTitle">
            <SparklesIcon
              height="24px"
              width="24px"
              style={{ marginRight: "8px" }}
            />
            Inspiration Hub
          </Typography>
          <Typography variant="body">
            Use this tool to generate new ideas and summaries based on the
            latest context from your families. You can save the results to
            documents for clients or future reference.
          </Typography>
        </FlexBox>

        <FlexBox direction="column" gap="24px">
          <FlexBox direction="column">
            {/* Family */}
            {adminMode ? (
              <AdminFamilySelect
                selectedFamily={selectedFamily}
                onChange={setSelectedFamily}
                title="Select Family"
                disabled={loading}
              />
            ) : (
              <FamilySelect
                selectedFamily={selectedFamily}
                onChange={setSelectedFamily}
                disabled={loading}
                sx={{
                  width: isMobile ? "100%" : "260px !important",
                  ".MuiSelect-icon": {
                    right: "14px",
                  },
                }}
              />
            )}

            {/* Configuration */}
            <Box
              sx={{
                border: "1px solid #D4D4D4",
                borderRadius: "12px",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  padding: "16px 20px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: configurationOpen
                    ? "#f9f9f9"
                    : "transparent",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
                onClick={() => setConfigurationOpen(!configurationOpen)}
              >
                <Typography variant="bodyHeavy">Configuration</Typography>
                <ChevronDown
                  size={20}
                  style={{
                    transform: configurationOpen
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                    transition: "transform 0.2s ease-in-out",
                  }}
                />
              </Box>
              <Collapse in={configurationOpen}>
                <FlexBox
                  direction="column"
                  sx={{
                    padding: "20px",
                  }}
                >
                  <FlexBox
                    stackedMobile
                    alignItems={isMobile ? "flex-start" : "center"}
                    gap={isMobile ? "0px" : "12px"}
                  >
                    <Typography variant="bodyHeavy" sx={{ ...formLeftStyle }}>
                      Information sources
                    </Typography>
                    <FlexBox sx={{ marginLeft: "-12px", ...formRightStyle }}>
                      <FormControlLabel
                        sx={{ marginRight: "0px" }}
                        control={
                          <Checkbox
                            checked={addTasks}
                            onChange={(e) => setAddTasks(e.target.checked)}
                            disabled={loading}
                          />
                        }
                        label="Tasks"
                      />
                      <FormControlLabel
                        sx={{ marginRight: "0px" }}
                        control={
                          <Checkbox
                            checked={addTaskActivity}
                            onChange={(e) =>
                              setAddTaskActivity(e.target.checked)
                            }
                            disabled={loading}
                          />
                        }
                        label="Task Activity"
                      />
                      <FormControlLabel
                        sx={{ marginRight: "0px" }}
                        control={
                          <Checkbox
                            checked={addFacts}
                            onChange={(e) => setAddFacts(e.target.checked)}
                            disabled={loading}
                          />
                        }
                        label="Facts"
                      />
                      <FormControlLabel
                        sx={{ marginRight: "0px" }}
                        control={
                          <Checkbox
                            checked={addNotes}
                            onChange={(e) => setAddNotes(e.target.checked)}
                            disabled={loading}
                          />
                        }
                        label="Notes"
                      />
                    </FlexBox>
                  </FlexBox>

                  {/* Time span */}
                  <ConfigurationSection
                    title="Time period"
                    content={
                      <FlexBox stackedMobile>
                        <FormControl
                          sx={{
                            minWidth: "200px",
                            flex: 1,
                            width: isMobile ? "100% !important" : "auto",
                          }}
                        >
                          <InputLabel>Events weeks forward</InputLabel>
                          <Select
                            value={eventsWeeksForward}
                            onChange={(e) =>
                              setEventsWeeksForward(Number(e.target.value))
                            }
                            disabled={loading}
                            label="Events weeks forward"
                          >
                            <MenuItem value={0}>No events</MenuItem>
                            <MenuItem value={1}>1 week</MenuItem>
                            <MenuItem value={2}>2 weeks</MenuItem>
                            <MenuItem value={4}>3 weeks</MenuItem>
                            <MenuItem value={8}>1 month</MenuItem>
                            <MenuItem value={12}>2 months</MenuItem>
                            <MenuItem value={16}>3 months</MenuItem>
                            <MenuItem value={24}>6 months</MenuItem>
                          </Select>
                        </FormControl>

                        <DatePicker
                          sx={{
                            flex: 1,
                            width: isMobile ? "100% !important" : "auto",
                            ".MuiButtonBase-root": { marginRight: "unset" },
                          }}
                          label="Activity since"
                          defaultValue={selectedDate || defaultDate()}
                          disabled={loading}
                          onChange={(date) => {
                            if (date) setSelectedDate(date);
                          }}
                          format="MM/dd/yyyy"
                          open={dateOpen}
                          onClose={() => setDateOpen(false)}
                          slotProps={{
                            textField: {
                              onClick: () => !loading && setDateOpen(true),
                            },
                          }}
                        />
                      </FlexBox>
                    }
                  />

                  {/* Document template */}
                  <ConfigurationSection
                    title="Document template"
                    description="The generated output will follow the same structure."
                    content={
                      <LoadDocument
                        onDocumentSelect={(doc) => {
                          setSelectedDocument(doc);
                          setDocumentId(doc?.id || "");
                        }}
                        disabled={loading}
                        initialDocumentId={documentId}
                        listOptions={true}
                        topics={["Template"]}
                      />
                    }
                  />

                  {/* Admin only options */}
                  {adminMode && (
                    <>
                      <Divider sx={{ width: "100%", margin: "12px 0 8px" }} />
                      <Typography variant="h4">Admin only</Typography>

                      <ConfigurationSection
                        title="Model"
                        content={
                          <FormControl
                            sx={{
                              minWidth: "200px",
                              ...formRightStyle,
                            }}
                          >
                            <InputLabel>Model</InputLabel>
                            <Select
                              value={selectedModel}
                              onChange={(e) =>
                                setSelectedModel(e.target.value as string)
                              }
                              disabled={loading}
                              label="Model"
                            >
                              <MenuItem value="">Auto</MenuItem>
                              {MODELS.map((model) => (
                                <MenuItem value={model.name}>
                                  {model.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        }
                      />
                    </>
                  )}
                </FlexBox>
              </Collapse>
            </Box>
          </FlexBox>
          {adminMode && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: adminMode ? "space-between" : "end",
              }}
            >
              <Button
                variant="contained"
                onClick={runContext}
                disabled={loading || !selectedFamily}
                sx={{ alignSelf: "flex-start" }}
              >
                {loading ? "Processing..." : "Generate Context"}
              </Button>
              <Button
                disabled={loading || !selectedFamily}
                variant="outlined"
                startIcon={<SparklesIcon />}
                onClick={() => setPromptOpen(!promptOpen)}
              >
                Run prompt
              </Button>
            </Box>
          )}

          <FlexBox direction="column">
            <SamplePrompts
              onPromptSelect={(selectedPrompt) => {
                setPrompt(selectedPrompt);
                setPromptOpen(true);
              }}
              disabled={loading}
            />

            <PromptSection
              open={promptOpen}
              query={prompt}
              setQuery={setPrompt}
              handleClick={runPrompt}
              loading={loading}
              disabled={loading || !selectedFamily || !prompt}
            />
          </FlexBox>
        </FlexBox>

        {documentData && (
          <Box
            sx={{
              border: "1px solid #EAEBEC",
              borderRadius: "8px",
              padding: "16px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "12px",
              }}
            >
              <Typography variant="bodyHeavy">{documentData.title}</Typography>
              <Box sx={{ display: "flex", gap: "4px" }}>
                <Tooltip title="Copy content">
                  <IconButton
                    size="small"
                    onClick={handleDocumentCopy}
                    sx={{
                      padding: "4px",
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    <Copy size={16} />
                  </IconButton>
                </Tooltip>
                {/* <Tooltip title="Download document">
                  <IconButton
                    size="small"
                    onClick={handleDocumentDownload}
                    sx={{
                      padding: "4px",
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    <Download size={16} />
                  </IconButton>
                </Tooltip> */}
                <Tooltip title="Create & edit document">
                  <IconButton
                    size="small"
                    onClick={handleDocumentEdit}
                    disabled={createDocumentLoading}
                    sx={{
                      padding: "4px",
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    <Pencil size={16} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Divider sx={{ marginBottom: "16px" }} />
            {documentData.contentType === "text" ? (
              <Typography
                variant="body"
                sx={{
                  whiteSpace: "pre-wrap",
                }}
              >
                {documentData.content}
              </Typography>
            ) : (
              <div id="document-viewer">
                <DocumentViewer
                  content={documentData.content}
                  maxHeight="400px"
                />
              </div>
            )}
          </Box>
        )}

        {output && (
          <Box
            sx={{
              padding: "16px",
              backgroundColor: "#f5f5f5",
              borderRadius: "8px",
              marginTop: "20px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "8px",
              }}
            >
              <Typography variant="bodyHeavy">
                {runMode === "context" ? "Context Result" : "Prompt Result"}
              </Typography>
              <Box sx={{ display: "flex", gap: "4px" }}>
                <Tooltip title="Copy to clipboard">
                  <IconButton
                    size="small"
                    onClick={handleCopy}
                    sx={{
                      padding: "4px",
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    <Copy size={16} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download as text file">
                  <IconButton
                    size="small"
                    onClick={handleDownload}
                    sx={{
                      padding: "4px",
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    <Download size={16} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Typography
              variant="body"
              component="pre"
              sx={{
                whiteSpace: "pre-wrap",
                fontFamily: "monospace",
                paddingTop: "20px",
              }}
            >
              {output}
            </Typography>
          </Box>
        )}

        {/* Alert upsell to share link */}
        {hasResults && (
          <Alert
            severity="info"
            icon={false}
            sx={(theme) => ({
              padding: "10px 20px 4px",
              backgroundColor: theme.palette.teal[1],
              border: `1px solid ${theme.palette.teal[2]}`,
              "& .MuiAlert-message": {
                width: "100%",
              },
            })}
          >
            <Box>
              <Typography variant="bodyHeavy">
                Happy with your results? Share the inspiration! 🎉
              </Typography>
              <Typography variant="body" sx={{ marginTop: "8px" }}>
                Share the link with other Advisors to let them use your prompt
                with their own clients.
              </Typography>
              <Button
                size="small"
                variant="text"
                startIcon={<Copy size={16} />}
                onClick={handleShareLinkCopy}
                sx={{
                  marginLeft: "6px",
                  minWidth: "auto",
                  whiteSpace: "nowrap",
                }}
              >
                Copy link
              </Button>
            </Box>
          </Alert>
        )}
      </Box>
    </GridPage>
  );
};
